import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/lib/AuthContext';
import { BadgeCatalogProvider } from '@/lib/BadgeCatalogContext';
import AppShell from '@/features/shell/AppShell';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import PwaPrompts from '@/features/pwa/PwaPrompts';

const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const SignupPage = lazy(() => import('./features/auth/SignupPage'));
const ForgotPasswordPage = lazy(
  () => import('./features/auth/ForgotPasswordPage'),
);
const ResetPasswordPage = lazy(
  () => import('./features/auth/ResetPasswordPage'),
);
const AuthCallbackPage = lazy(
  () => import('./features/auth/AuthCallbackPage'),
);
const OnboardingPage = lazy(() => import('./features/auth/OnboardingPage'));
const ArtistHubPage = lazy(
  () => import('./features/artist/ArtistHubPage'),
);
const FeedTab = lazy(() => import('./features/artist/tabs/FeedTab'));
const BoardsList = lazy(() => import('./features/boards/BoardsList'));
const BoardThreads = lazy(
  () => import('./features/boards/BoardThreads'),
);
const CreatePostPage = lazy(
  () => import('./features/boards/CreatePostPage'),
);
const CreateBoardPage = lazy(
  () => import('./features/boards/CreateBoardPage'),
);
const BoardSettingsPage = lazy(
  () => import('./features/boards/BoardSettingsPage'),
);
const ChatRoomsList = lazy(
  () => import('./features/chat/ChatRoomsList'),
);
const ChatRoomPage = lazy(() => import('./features/chat/ChatRoomPage'));
const CreateChatRoomPage = lazy(
  () => import('./features/chat/CreateChatRoomPage'),
);
const PostDetailPage = lazy(
  () => import('./features/boards/PostDetailPage'),
);
const MembershipPage = lazy(
  () => import('./features/membership/MembershipPage'),
);
const BillingSuccessPage = lazy(
  () => import('./features/membership/BillingSuccessPage'),
);
const SellerPage = lazy(() => import('./features/seller/SellerPage'));
const CreateListingPage = lazy(
  () => import('./features/seller/CreateListingPage'),
);
const StoreTab = lazy(() => import('./features/store/StoreTab'));
const ProductDetailPage = lazy(
  () => import('./features/store/ProductDetailPage'),
);
const MyOrdersPage = lazy(() => import('./features/orders/MyOrdersPage'));
const ModerationPage = lazy(
  () => import('./features/admin/ModerationPage'),
);
const StreamsTab = lazy(() => import('./features/streams/StreamsTab'));
const StreamDetailPage = lazy(
  () => import('./features/streams/StreamDetailPage'),
);
const CreateStreamPage = lazy(
  () => import('./features/admin/CreateStreamPage'),
);
const ListeningTab = lazy(
  () => import('./features/listening/ListeningTab'),
);
const ListeningRoomDetailPage = lazy(
  () => import('./features/listening/ListeningRoomDetailPage'),
);
const CreateListeningRoomPage = lazy(
  () => import('./features/admin/CreateListeningRoomPage'),
);
const ArtistStudioPage = lazy(
  () => import('./features/artist/ArtistStudioPage'),
);
const ArtistSettingsPage = lazy(
  () => import('./features/artist/ArtistSettingsPage'),
);
const TeamManagementPage = lazy(
  () => import('./features/artist/TeamManagementPage'),
);
const TourTab = lazy(() => import('./features/tour/TourTab'));
const CreatePresalePage = lazy(
  () => import('./features/artist/CreatePresalePage'),
);
const NotificationsSettingsPage = lazy(
  () => import('./features/settings/NotificationsSettingsPage'),
);
const ProfileSettingsPage = lazy(
  () => import('./features/account/ProfileSettingsPage'),
);
const PublicProfilePage = lazy(
  () => import('./features/profile/PublicProfilePage'),
);
const RewardsPage = lazy(() => import('./features/rewards/RewardsPage'));
const MerchDropsList = lazy(
  () => import('./features/drops/MerchDropsList'),
);
const MerchDropDetail = lazy(
  () => import('./features/drops/MerchDropDetail'),
);
const StudioDropsPage = lazy(
  () => import('./features/drops/StudioDropsPage'),
);
const StudioDropEditPage = lazy(
  () => import('./features/drops/StudioDropEditPage'),
);
const SellerOrdersPage = lazy(
  () => import('./features/seller/SellerOrdersPage'),
);
const BoardsAdminPage = lazy(
  () => import('./features/artist/BoardsAdminPage'),
);
const OrderDetailPage = lazy(
  () => import('./features/orders/OrderDetailPage'),
);
const ArtistAnalyticsPage = lazy(
  () => import('./features/artist/ArtistAnalyticsPage'),
);
const SearchPage = lazy(() => import('./features/search/SearchPage'));
const BookmarksPage = lazy(
  () => import('./features/bookmarks/BookmarksPage'),
);
const ArtistMarketplacePage = lazy(
  () => import('./features/discover/ArtistMarketplacePage'),
);
const PrivacyPolicyPage = lazy(
  () => import('./features/legal/PrivacyPolicyPage'),
);
const TermsOfServicePage = lazy(
  () => import('./features/legal/TermsOfServicePage'),
);
const BirthdaysPage = lazy(() => import('./features/admin/BirthdaysPage'));
const AddToHomeScreenPage = lazy(
  () => import('./features/pwa/AddToHomeScreenPage'),
);

function App() {
  return (
    <AuthProvider>
      <BadgeCatalogProvider>
      <PwaPrompts />
      <Suspense fallback={<ScaffoldFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/auth/reset" element={<ResetPasswordPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/add-to-home-screen" element={<AddToHomeScreenPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />

          {/* All other routes get the persistent TopBar via AppShell. */}
          <Route element={<AppShell />}>
          <Route path="/" element={<ArtistMarketplacePage />} />
          <Route path="/artists" element={<Navigate to="/" replace />} />
          <Route path="/search" element={<SearchPage />} />

          <Route path="/membership" element={<MembershipPage />} />
          <Route
            path="/billing/success"
            element={
              <ProtectedRoute>
                <BillingSuccessPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/seller"
            element={
              <ProtectedRoute>
                <SellerPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/new"
            element={
              <ProtectedRoute>
                <CreateListingPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seller/orders"
            element={
              <ProtectedRoute>
                <SellerOrdersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/orders"
            element={
              <ProtectedRoute>
                <MyOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookmarks"
            element={
              <ProtectedRoute>
                <BookmarksPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <ProtectedRoute>
                <OrderDetailPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute requireAdmin>
                <ModerationPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/birthdays"
            element={
              <ProtectedRoute requireAdmin>
                <BirthdaysPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/streams/new"
            element={
              <ProtectedRoute requireAdmin>
                <CreateStreamPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/listening-rooms/new"
            element={
              <ProtectedRoute requireAdmin>
                <CreateListeningRoomPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio"
            element={
              <ProtectedRoute>
                <ArtistStudioPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/settings"
            element={
              <ProtectedRoute>
                <ArtistSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/team"
            element={
              <ProtectedRoute>
                <TeamManagementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/presales/new"
            element={
              <ProtectedRoute>
                <CreatePresalePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/boards"
            element={
              <ProtectedRoute>
                <BoardsAdminPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/drops"
            element={
              <ProtectedRoute>
                <StudioDropsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artists/:slug/studio/drops/new"
            element={
              <ProtectedRoute>
                <StudioDropEditPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/artists/:slug/studio/drops/:dropSlug/edit"
            element={
              <ProtectedRoute>
                <StudioDropEditPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug/studio/analytics"
            element={
              <ProtectedRoute>
                <ArtistAnalyticsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <NotificationsSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings/profile"
            element={
              <ProtectedRoute>
                <ProfileSettingsPage />
              </ProtectedRoute>
            }
          />

          <Route path="/@:handle" element={<PublicProfilePage />} />

          <Route
            path="/rewards"
            element={
              <ProtectedRoute>
                <RewardsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <OnboardingPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/artists/:slug"
            element={
              <ProtectedRoute requirePremium>
                <ArtistHubPage />
              </ProtectedRoute>
            }
          >
            <Route index element={<FeedTab />} />
            <Route path="community" element={<BoardsList />} />
            <Route
              path="community/new"
              element={
                <ProtectedRoute>
                  <CreateBoardPage />
                </ProtectedRoute>
              }
            />
            <Route path="community/:boardSlug" element={<BoardThreads />} />
            <Route
              path="community/:boardSlug/new"
              element={
                <ProtectedRoute>
                  <CreatePostPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="community/:boardSlug/settings"
              element={
                <ProtectedRoute>
                  <BoardSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="community/:boardSlug/:postId"
              element={<PostDetailPage />}
            />
            <Route path="chat" element={<ChatRoomsList />} />
            <Route
              path="chat/new"
              element={
                <ProtectedRoute>
                  <CreateChatRoomPage />
                </ProtectedRoute>
              }
            />
            <Route path="chat/:roomSlug" element={<ChatRoomPage />} />
            <Route path="live" element={<StreamsTab />} />
            <Route path="live/:streamId" element={<StreamDetailPage />} />
            <Route path="music" element={<ListeningTab />} />
            <Route path="music/:roomId" element={<ListeningRoomDetailPage />} />
            <Route path="store" element={<StoreTab />} />
            <Route path="store/:productId" element={<ProductDetailPage />} />
            <Route path="drops" element={<MerchDropsList />} />
            <Route path="drops/:dropSlug" element={<MerchDropDetail />} />
            <Route path="tour" element={<TourTab />} />
          </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
      </BadgeCatalogProvider>
    </AuthProvider>
  );
}

function ScaffoldFallback() {
  return (
    <div className="flex h-full items-center justify-center bg-surface">
      <div className="size-8 animate-pulse rounded-full bg-primary/40" />
    </div>
  );
}

function NotFound() {
  return (
    <main className="flex h-full flex-col items-center justify-center gap-3 bg-surface px-6 text-center text-on-surface">
      <h1 className="font-display text-3xl">Not found</h1>
      <p className="text-muted">This room hasn&rsquo;t opened yet.</p>
    </main>
  );
}

export default App;
